import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { useMyAgentTenders } from "../../hooks/agent-tender/useMyAgentTenders";
import { useAgentTenderResponses } from "../../hooks/agent-tender/useAgentTenderResponses";
import AgentTenderStatusPill from "../../components/agent-tender/AgentTenderStatusPill";
import AgentTenderPreviewModal from "../../components/agent-tender/AgentTenderPreviewModal";
import VerifyNowModal from "../../components/agent-tender/VerifyNowModal";
import { getCachedVerifyLinks } from "../../components/agent-tender/verifyLinkCache";
import { agentTenderService } from "../../services/agentTenderApi";
import ResponseRow from "../../components/agent-tender/ResponseRow";
import CategoryPills from "../../components/agent-tender/CategoryPills";
import CustomDropdown from "../../components/common/CustomDropdown";
import v2DropdownProps from "../../components/agent-tender/v2DropdownProps";
import LoanBuySwitch from "../../components/common/LoanBuySwitch";
import {
  MY_REQUESTS_TABS,
  FEED_LEVEL_CHOICES,
  FEED_SORT_CHOICES,
  formatPositionValue,
  formatTypeOfTender,
  formatStaffRole,
} from "../../constants/agentTenderChoices";

const Pagination = ({ pagination, onPage }) => {
  if (!pagination || pagination.count <= 0) return null;
  const { currentPage, next, previous } = pagination;
  if (!next && !previous) return null;
  return (
    <div className="flex justify-center items-center gap-4 mt-6">
      <button
        disabled={!previous}
        onClick={() => onPage(currentPage - 1)}
        className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
      >
        Previous
      </button>
      <span className="text-sm text-gray-600">Page {currentPage}</span>
      <button
        disabled={!next}
        onClick={() => onPage(currentPage + 1)}
        className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
      >
        Next
      </button>
    </div>
  );
};

// ── Artboard SVGs (design doc 03, screen 7 — verbatim, Rule 2) ──────────────
const IconArchive = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="5" rx="1" />
    <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8M10 12h4" />
  </svg>
);
const IconQr = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <path d="M14 14h3v3M21 14v7h-7" />
  </svg>
);

// "Close & archive" confirm state (V2, design doc 03 screen 7): a status
// transition, never a delete — the archive is the Expired tab. Desktop is
// the 480px modal, ≤640 the bottom sheet (stacked buttons, short body).
export const CloseConfirm = ({ tender, onCancel, onConfirm, busy }) => {
  if (!tender) return null;
  const contextPill = [
    tender.tender_category === "staff"
      ? formatStaffRole(tender.staff_role, tender.other_staff_position)
      : formatPositionValue(tender.position, tender.other_staff_position),
    formatTypeOfTender(tender.type_of_tender),
    tender.club?.name,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="fixed inset-0 bg-[rgba(15,23,42,0.42)] flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-[18px] sm:rounded-2xl w-full sm:max-w-[480px] px-4 pb-5 pt-2.5 sm:p-[26px] shadow-[0_24px_64px_rgba(15,23,42,0.28)] flex flex-col gap-[13px]">
        <div className="flex justify-center sm:hidden">
          <span className="w-9 h-1 rounded-full bg-[#E2E8F0]" />
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-flex w-[42px] h-[42px] rounded-full bg-[#F1F5F9] text-[#475569] items-center justify-center flex-shrink-0">
            <IconArchive size={18} />
          </span>
          <h3 className="text-[16.5px] sm:text-[17px] font-extrabold tracking-tight">
            Close &amp; archive this request?
          </h3>
        </div>
        {contextPill && (
          <span className="self-start text-[11px] font-semibold px-[9px] py-[3px] rounded-full bg-[#F1F5F9] text-[#475569]">
            {contextPill}
          </span>
        )}
        <p className="text-[12.5px] sm:text-[13px] font-semibold text-[#334155] bg-[#F8FAFC] border border-[#EEF2F7] rounded-[11px] px-3.5 py-3">
          Removed from the marketplace · chats, responses and history are kept.
        </p>
        <p className="hidden sm:block text-xs text-[#64748B] leading-relaxed">
          It moves to your archive (Expired tab) and can be reviewed anytime. A
          Club Verified badge ends with the request.
        </p>
        <p className="sm:hidden text-[11.5px] text-[#64748B] leading-relaxed">
          Moves to your archive (Expired tab).
        </p>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-2.5 sm:mt-0.5">
          <button
            onClick={onConfirm}
            disabled={busy}
            className="order-1 sm:order-2 h-[46px] sm:h-[42px] px-[18px] bg-[#334155] text-white rounded-[11px] sm:rounded-[10px] text-[13.5px] sm:text-[13px] font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-[7px]"
          >
            <IconArchive />
            {busy ? "Archiving…" : "Close & archive"}
          </button>
          <button
            onClick={onCancel}
            className="order-2 sm:order-1 h-[42px] px-4 border border-[#E2E8F0] bg-white text-[#334155] rounded-[11px] sm:rounded-[10px] text-[13px] font-semibold hover:bg-gray-50 inline-flex items-center justify-center"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

// Verification journey V2 (design doc 03 screen 7) — five states, from the
// Mine serializer (verification_status, superseded_by, verification_journey
// {status,created_at,decline_reason}). V2 lifecycle: NO "Verified until", NO
// renewal — the badge lives exactly as long as the request. Resend/try-again
// reuse the SAME modal flow (one CTA path); "Show QR again" re-opens the
// armed surface from the client link cache WITHOUT resending anything.
//
// Presentation per artboard: the journey is the grey inner box of the row —
// optional state glyph in a 26px circle (✉ / ! / ✓ / ⇅, verbatim unicode),
// message + sub line, CTAs to the right (desktop) or wrapped below (mobile).
// "Close & archive" is the quiet trailing CTA of every ACTIVE journey row.
const outlineCta =
  "flex-none h-[34px] px-3 rounded-lg text-xs font-semibold border border-[#E2E8F0] bg-white text-[#334155] hover:bg-gray-50 inline-flex items-center gap-1.5";
const primaryCta =
  "flex-none h-[34px] px-3.5 rounded-lg text-xs font-semibold bg-[#047857] text-white hover:bg-emerald-800 inline-flex items-center";
const archiveCta =
  "flex-none h-[34px] px-3 rounded-lg text-xs font-semibold border border-[#E2E8F0] bg-white text-[#64748B] hover:bg-gray-50 inline-flex items-center gap-1.5";

// State glyph circle (artboard: 26px, tinted per state, unicode glyph).
const StateGlyph = ({ glyph, bg, color }) => (
  <span
    className="flex-none w-[26px] h-[26px] rounded-full inline-flex items-center justify-center text-xs font-extrabold"
    style={{ background: bg, color }}
    aria-hidden="true"
  >
    {glyph}
  </span>
);

// Owner-side certificate download (authed; works after archive too).
const downloadOwnerCertificate = async (tender) => {
  const res = await agentTenderService.downloadCertificate(tender.id);
  if (!res.success) {
    toast.error(res.error || "Couldn't download the confirmation PDF.");
    return;
  }
  const url = URL.createObjectURL(res.blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `TransferPitch confirmation - REQ-${tender.id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const VerificationJourney = ({ tender, onVerify, onShowQr, onClose }) => {
  const vs = tender.verification_status;
  const journey = tender.verification_journey || null;
  const jstatus = journey && journey.status;
  const active = tender.status === "active";
  // "Show QR again" only when this browser still holds the armed links —
  // the backend stores only the token hash and can never re-issue them.
  const hasCachedLinks = !!getCachedVerifyLinks(tender.id);

  // The grey journey box (artboard): glyph circle, message + sub, CTAs.
  // Inline on desktop, message above the wrapped CTA row on mobile.
  //
  // The row engages at min-[1100px], not at md (QAF01-050). The card spends
  // 300px + 110px on two fixed columns before this box gets anything, and the
  // CTA cluster below has no shrink allowance — so between 768 and ~1080 this
  // box's `min-w-0` text column was the only thing left to give and measured
  // 0px: the message escaped its box, the neighbouring button sat on top of
  // it, and the card grew to 293px. Measured per width, the row only becomes
  // healthy from ~1100 (1024 still yielded a 32px column with the text
  // overflowing, so `lg:` would not have fixed it).
  const box = ({ glyph, msg, msgColor, sub, ctas }) => (
    <div className="flex-1 min-w-0 bg-[#F8FAFC] border border-[#EEF2F7] rounded-[10px] px-3.5 py-2.5 flex flex-col min-[1100px]:flex-row min-[1100px]:items-center gap-2 min-[1100px]:gap-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {glyph && <StateGlyph {...glyph} />}
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-semibold" style={{ color: msgColor }}>
            {msg}
          </p>
          {sub && <p className="text-[11.5px] text-[#94A3B8] mt-0.5">{sub}</p>}
        </div>
      </div>
      {ctas && ctas.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">{ctas}</div>
      )}
    </div>
  );

  // "Close & archive" — quiet trailing CTA on every ACTIVE journey row
  // (unverified, awaiting, declined, verified). Inactive rows never show it.
  const closeCta =
    active && onClose ? (
      <button
        key="close"
        type="button"
        onClick={() => onClose(tender)}
        className={archiveCta}
      >
        <IconArchive size={12} /> Close &amp; archive
      </button>
    ) : null;

  if (vs === "club_verified") {
    return box({
      glyph: { glyph: "✓", bg: "#ECFDF5", color: "#047857" },
      msg: active
        ? "Club verified · stays verified while this request is active."
        : "Club verified · this request is no longer active.",
      msgColor: "#065F46",
      sub: "+30 TransferPoints earned",
      ctas: [
        <button
          key="pdf"
          type="button"
          onClick={() => downloadOwnerCertificate(tender)}
          className={outlineCta}
        >
          Confirmation PDF
        </button>,
        closeCta,
      ].filter(Boolean),
    });
  }
  if (vs === "superseded" || tender.superseded_by) {
    return box({
      glyph: { glyph: "⇅", bg: "#FFFBEB", color: "#B45309" },
      msg: "Superseded by a newer verified request for the same need.",
      msgColor: "#78350F",
      sub: "Archived, not deleted.",
    });
  }
  if (vs === "revoked_fraud") {
    return box({
      glyph: { glyph: "!", bg: "#FEE2E2", color: "#B91C1C" },
      msg: "Verification was revoked.",
      msgColor: "#B91C1C",
    });
  }
  if (jstatus === "pending" || jstatus === "otp_sent") {
    return box({
      glyph: { glyph: "✉", bg: "#E8F1FF", color: "#1B6FE0" },
      msg: `Verification sent${journey.created_at ? ` on ${fmtDate(journey.created_at)}` : ""} · awaiting the club.`,
      msgColor: "#334155",
      sub: "The club contact has the link and QR.",
      ctas: [
        active && hasCachedLinks && (
          <button
            key="qr"
            type="button"
            onClick={() => onShowQr(tender)}
            className={outlineCta}
          >
            <IconQr /> Show QR again
          </button>
        ),
        active && (
          <button
            key="resend"
            type="button"
            onClick={() => onVerify(tender)}
            className={outlineCta}
          >
            Resend
          </button>
        ),
        closeCta,
      ].filter(Boolean),
    });
  }
  if (jstatus === "declined") {
    return box({
      glyph: { glyph: "!", bg: "#FEE2E2", color: "#B91C1C" },
      msg: "Declined by the club.",
      msgColor: "#B91C1C",
      sub: journey.decline_reason
        ? `Reason given: "${journey.decline_reason}"`
        : undefined,
      ctas: [
        active && (
          <button
            key="retry"
            type="button"
            onClick={() => onVerify(tender)}
            className={primaryCta}
          >
            Try again
          </button>
        ),
        closeCta,
      ].filter(Boolean),
    });
  }
  if (!active) {
    return box({
      msg: "This request is no longer active.",
      msgColor: "#64748B",
      sub: "Closed requests are archived, not deleted.",
    });
  }
  // Unverified and active → prompt to start verification.
  return box({
    msg: "Not verified yet.",
    msgColor: "#334155",
    sub: "Verify to get the badge and rank first.",
    ctas: [
      <button
        key="verify"
        type="button"
        onClick={() => onVerify(tender)}
        className={primaryCta}
      >
        Verify now
      </button>,
      closeCta,
    ].filter(Boolean),
  });
};

// Journey row (artboard 1440: title block · status pill · journey box; 390:
// stacked card with the status pill in the title row). "Close & archive"
// lives INSIDE the journey box (quiet trailing CTA on active rows); the
// loupe + Edit affordances are kept as quiet controls — the handoff is
// silent about them.
const TenderRow = ({ tender, onPreview, onVerify, onShowQr, onEdit, onClose }) => {
  const title =
    tender.tender_category === "staff"
      ? formatStaffRole(tender.staff_role, tender.other_staff_position)
      : formatPositionValue(tender.position, tender.other_staff_position);
  const club = tender.club;
  const meta = [
    formatTypeOfTender(tender.type_of_tender),
    formatTypeOfTender(tender.recruiting_for),
  ]
    .filter(Boolean)
    .join(" · ");
  const statusPill = (
    <AgentTenderStatusPill
      status={tender.status}
      verificationStatus={tender.verification_status}
    />
  );
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] px-4 md:px-[18px] py-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.05)] flex flex-col min-[1100px]:flex-row min-[1100px]:items-center gap-2.5 min-[1100px]:gap-4">
      <div className="min-[1100px]:w-[300px] min-[1100px]:flex-none min-w-0">
        <div className="flex items-center gap-[7px] flex-wrap">
          {/* No `capitalize`: staff role "other" carries the agent's own
              typed title (T059). */}
          <h3 className="text-[14.5px] font-bold">{title}</h3>
          {tender.type_of_tender && (
            <span
              className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full capitalize ${typePillClass(
                tender.type_of_tender,
              )}`}
            >
              {formatTypeOfTender(tender.type_of_tender)}
            </span>
          )}
          <span className="flex-1 min-[1100px]:hidden" />
          <span className="min-[1100px]:hidden">{statusPill}</span>
        </div>
        {/* Internal view: the club is visible to its own agency only. */}
        {(club?.name || meta) && (
          <div className="flex items-center gap-1.5 mt-1 min-w-0">
            {club?.name &&
              (club.logo ? (
                <img
                  src={club.logo}
                  alt=""
                  className="w-5 h-5 rounded-md object-contain bg-white flex-none"
                />
              ) : (
                <span className="w-5 h-5 rounded-md bg-[#0F766E] text-white text-[9px] font-extrabold inline-flex items-center justify-center flex-none">
                  {clubInitials(club.name)}
                </span>
              ))}
            {club?.name && (
              <span className="text-xs text-[#475569] truncate">{club.name}</span>
            )}
            {meta && (
              <span className="text-[11.5px] text-[#94A3B8] truncate">· {meta}</span>
            )}
          </div>
        )}
      </div>
      <div className="hidden min-[1100px]:block min-[1100px]:w-[110px] flex-none">
        {statusPill}
      </div>
      {/* Verification journey V2 — all states visible + actionable. */}
      <VerificationJourney
        tender={tender}
        onVerify={onVerify}
        onShowQr={onShowQr}
        onClose={onClose}
      />
      <div className="flex items-center gap-2 flex-none justify-end">
        {/* Loupe: read-only internal preview (full view — club included). */}
        <button
          onClick={() => onPreview(tender)}
          className="w-[34px] h-[34px] border border-[#E2E8F0] rounded-lg inline-flex items-center justify-center text-[#64748B] hover:bg-gray-50"
          aria-label="Preview request"
          title="Quick preview"
        >
          <Search className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onEdit(tender)}
          className="h-[34px] px-3 border border-[#E2E8F0] rounded-lg text-xs font-semibold text-[#334155] hover:bg-gray-50"
        >
          Edit
        </button>
      </div>
    </div>
  );
};

// ── Responses inbox V2 (design doc 03, screen 7b) ────────────────────────
// One container per request; header = request title + type pill + club chip
// (internal view — the poster owns this club) + response count; the V2 triage
// rows sit inside. Collapsing a group is presentation only.
const typePillClass = (type) => {
  if (type === "buy") return "bg-[#E8F1FF] text-[#1B6FE0]";
  if (type === "loan") return "bg-[#EEF2FF] text-[#4F46E5]";
  if (type === "trial") return "bg-[#FEF3C7] text-[#B45309]";
  return "bg-[#F1F5F9] text-[#334155]";
};

// The inbox tender ref carries no staff_role, so staff requests fall back to
// the position fields it does carry (same rule as the row's origin chip).
const requestTitle = (t) =>
  t?.tender_category === "staff"
    ? formatStaffRole(t.staff_role) ||
      formatPositionValue(t.position, t.other_staff_position)
    : formatPositionValue(t?.position, t?.other_staff_position);

const clubInitials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export const ResponseGroup = ({ tender, rows, renderRow }) => {
  const [open, setOpen] = useState(true);
  const club = tender?.club;
  const count = rows.length;
  return (
    <div
      className={`bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm ${
        open ? "" : "opacity-75"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 lg:px-[18px] py-3 bg-[#F8FAFC] border-b border-[#EEF2F7] text-left"
      >
        {/* No `capitalize` — see the row title above (T059). */}
        <span className="text-[14px] font-extrabold text-[#0F172A] truncate">
          {requestTitle(tender) || "Request"}
        </span>
        {tender?.type_of_tender && (
          <span
            className={`flex-none text-[10.5px] font-bold px-2 py-0.5 rounded-full capitalize ${typePillClass(
              tender.type_of_tender,
            )}`}
          >
            {formatTypeOfTender(tender.type_of_tender)}
          </span>
        )}
        {club?.name && (
          <span className="hidden sm:inline-flex items-center gap-1.5 min-w-0">
            {club.logo ? (
              <img
                src={club.logo}
                alt=""
                className="w-[18px] h-[18px] rounded object-contain bg-white flex-none"
              />
            ) : (
              <span className="w-[18px] h-[18px] rounded bg-[#0F766E] text-white text-[8px] font-extrabold inline-flex items-center justify-center flex-none">
                {clubInitials(club.name)}
              </span>
            )}
            <span className="text-[12px] text-[#475569] truncate">{club.name}</span>
          </span>
        )}
        <span className="flex-1" />
        <span className="text-[12px] text-[#64748B] whitespace-nowrap">
          {open
            ? count === 1
              ? "1 response · newest first"
              : `${count} responses · newest first`
            : count === 1
            ? "1 response"
            : `${count} responses`}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#64748B] flex-none transition-transform ${
            open ? "" : "-rotate-90"
          }`}
        />
      </button>
      {open && rows.map(renderRow)}
    </div>
  );
};

// Group a response list by its request, newest response first — inside each
// group and across groups. Pure presentation over the one fetched list.
const byNewest = (a, b) =>
  +new Date(b.created_at || 0) - +new Date(a.created_at || 0);
export const groupByTender = (list) => {
  const map = new Map();
  for (const r of list) {
    const key = r.tender?.id ?? "none";
    if (!map.has(key)) map.set(key, { tender: r.tender, rows: [] });
    map.get(key).rows.push(r);
  }
  const groups = [...map.values()];
  groups.forEach((g) => g.rows.sort(byNewest));
  groups.sort((a, b) => byNewest(a.rows[0], b.rows[0]));
  return groups;
};

const EmptyBlock = ({ title, body }) => (
  <div className="flex flex-col items-center py-16 text-center">
    <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
    <p className="text-gray-500">{body}</p>
  </div>
);

// `embedded` = rendered inside the Agent Requests tab shell (AgentTenderFeed):
// the shell owns the page chrome + the top My/All/Saved tab bar, so we drop our
// own page header + Post button and the full-screen wrapper to avoid doubling.
const MyAgentTenders = ({ embedded = false }) => {
  const navigate = useNavigate();
  const {
    tenders,
    loading,
    error,
    closingId,
    closeTender,
    activeCategory,
    changeCategory,
    filters,
    applyFilters,
    tabCounts,
    pagination,
    fetchMine,
  } = useMyAgentTenders();
  const {
    responses,
    loading: responsesLoading,
    error: responsesError,
    busyId,
    accept,
    reject,
    toggleShortlist,
  } = useAgentTenderResponses();

  // Tab can be preselected from a notification deep-link (?tab=responses).
  const search = new URLSearchParams(window.location.search);
  const initialTab = search.get("tab") || "all";
  // Optional deep-link scope: the Commissioned-Search "View request · N replies"
  // action lands here with ?tab=responses&tender=<id> to show just that request's
  // replies. Purely additive and param-guarded — with no ?tender the Responses
  // tab behaves EXACTLY as before.
  const tenderParam = search.get("tender");
  const [activeTab, setActiveTab] = useState(
    MY_REQUESTS_TABS.some((t) => t.key === initialTab) ? initialTab : "all",
  );
  const [verifyFor, setVerifyFor] = useState(null);
  const [closeFor, setCloseFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);

  // The Responses inbox (design 03 screen 7b): everything offered to your
  // requests EXCEPT what you declined — accepted rows stay visible with their
  // green state + "Open chat" (previously they vanished from every tab).
  // Un-actioned responses are status "active" (Pitch.status default — there
  // is no "pending" value); Declined = rejected.
  const openResponses = useMemo(
    () => responses.filter((r) => r.status !== "rejected"),
    [responses],
  );
  // Responses shown in the Responses tab: all of them, unless a ?tender scope is
  // set (the CS deep-link), in which case only that request's group. The tab
  // COUNT stays openResponses.length — scoping is a view filter, not a data
  // change — so the rest of the page is untouched.
  const scopedResponses = useMemo(
    () =>
      tenderParam
        ? openResponses.filter((r) => String(r.tender?.id) === String(tenderParam))
        : openResponses,
    [openResponses, tenderParam],
  );
  const shortlisted = useMemo(
    () => responses.filter((r) => r.is_shortlisted && r.status !== "rejected"),
    [responses],
  );
  const declined = useMemo(
    () => responses.filter((r) => r.status === "rejected"),
    [responses],
  );
  const expiredTenders = useMemo(
    () => tenders.filter((t) => t.status === "expired"),
    [tenders],
  );

  // Every category, not just the one on screen (QAF01-051). `tenders` is what
  // the Mine endpoint returned for the ACTIVE category and the CURRENT page —
  // counting it made the "All" tab echo the selected pill and change whenever
  // the category changed. `tabCounts` is the server's per-category total and
  // is what the category pills themselves render (`counts={tabCounts}` below),
  // so summing it is the only figure that can agree with them.
  const totalMine =
    (tabCounts.players || 0) +
    (tabCounts.coaches || 0) +
    (tabCounts.staffs || 0) +
    (tabCounts.others || 0);

  const tabCount = (key) => {
    if (key === "all") return totalMine;
    if (key === "responses") return openResponses.length;
    if (key === "shortlist") return shortlisted.length;
    if (key === "expired") return expiredTenders.length;
    if (key === "declined") return declined.length;
    return 0;
  };

  const renderResponseRow = (r, extra = {}) => (
    <ResponseRow
      key={r.id}
      response={r}
      busy={busyId === r.id}
      onAccept={accept}
      onReject={reject}
      onToggleShortlist={toggleShortlist}
      {...extra}
    />
  );

  // Responses/Declined: rows grouped per request. Shortlist: one flat list
  // across requests, each row carrying its "From: [request]" origin chip.
  const responseGroups = (list) => (
    <div className="space-y-3">
      {groupByTender(list).map((g) => (
        <ResponseGroup
          key={g.tender?.id ?? "none"}
          tender={g.tender}
          rows={g.rows}
          renderRow={renderResponseRow}
        />
      ))}
    </div>
  );

  const responseFlatList = (list) => (
    <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm">
      {[...list].sort(byNewest).map((r) => renderResponseRow(r, { showOrigin: true }))}
    </div>
  );

  const renderTab = () => {
    const rowList = (list) => (
      <div className="space-y-4">
        {list.map((t) => (
          <TenderRow
            key={t.id}
            tender={t}
            onPreview={setPreviewFor}
            onVerify={setVerifyFor}
            // "Show QR again" opens the SAME surface — the link cache makes it
            // come up armed, so nothing is resent.
            onShowQr={setVerifyFor}
            onEdit={(tender) => navigate(`/agent-tenders/${tender.id}/edit`)}
            onClose={setCloseFor}
          />
        ))}
      </div>
    );

    if (activeTab === "expired") {
      if (loading) return <div className="text-gray-500 py-16 text-center">Loading…</div>;
      if (error) return <div className="text-red-500 py-16 text-center">{error}</div>;
      return expiredTenders.length === 0 ? (
        <EmptyBlock
          title="No expired requests"
          body="Requests you close or that reach their window's end show up here."
        />
      ) : (
        rowList(expiredTenders)
      );
    }

    if (activeTab === "all") {
      // Full feed-parity browse (P9): the same category pills / level / type /
      // sort controls + pagination + counts as the public feed, driven by the
      // Mine endpoint's matching params.
      return (
        <>
          <CategoryPills
            activeCategory={activeCategory}
            onChange={changeCategory}
            counts={tabCounts}
          />
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <LoanBuySwitch
              value={filters.type_of_tender || "all"}
              onChange={(v) =>
                applyFilters({ type_of_tender: v === "all" ? "" : v })
              }
            />
            <CustomDropdown
              options={FEED_LEVEL_CHOICES}
              value={filters.recruiting_for || ""}
              onChange={(v) => applyFilters({ recruiting_for: v })}
              placeholder="All levels"
              displayProperty="label"
              valueProperty="value"
              {...v2DropdownProps(false)}
              className="min-w-[130px] border-[#E2E8F0] rounded-[9px] whitespace-nowrap"
            />
            <CustomDropdown
              options={FEED_SORT_CHOICES}
              value={filters.sort_by || "newest"}
              onChange={(v) => applyFilters({ sort_by: v })}
              displayProperty="label"
              valueProperty="value"
              {...v2DropdownProps(false)}
              className="min-w-[160px] border-[#E2E8F0] rounded-[9px] whitespace-nowrap"
            />
          </div>
          {loading ? (
            <div className="text-gray-500 py-16 text-center">Loading…</div>
          ) : error ? (
            <div className="text-red-500 py-16 text-center">{error}</div>
          ) : tenders.length === 0 ? (
            totalMine === 0 ? (
              <div className="flex flex-col items-center py-16 text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  You haven't posted any requests yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Post your first request to start reaching clubs.
                </p>
                <button
                  onClick={() => navigate("/agent-tenders/create")}
                  className="px-4 py-2 bg-[#2283FF] text-white rounded-lg text-sm hover:bg-blue-600"
                >
                  Post Request
                </button>
              </div>
            ) : (
              <EmptyBlock
                title="No requests in this category"
                body="Try another category or adjust the filters above."
              />
            )
          ) : (
            <>
              {rowList(tenders)}
              <Pagination pagination={pagination} onPage={(p) => fetchMine(p)} />
            </>
          )}
        </>
      );
    }

    // Response-backed tabs
    if (responsesLoading) return <div className="text-gray-500 py-16 text-center">Loading…</div>;
    if (responsesError) return <div className="text-red-500 py-16 text-center">{responsesError}</div>;

    if (activeTab === "responses") {
      // Scoped-view notice + escape hatch (only when a ?tender scope is active).
      const scopeBanner = tenderParam ? (
        <div className="flex items-center justify-between gap-3 mb-3 px-3.5 py-2.5 bg-[#F1F5F9] border border-[#E2E8F0] rounded-lg">
          <span className="text-[12.5px] text-[#475569]">
            Showing responses for one request.
          </span>
          <button
            type="button"
            onClick={() => navigate("/agent-tenders/mine?tab=responses")}
            className="text-[12.5px] font-semibold text-[#2283FF] hover:underline whitespace-nowrap"
          >
            Show all responses
          </button>
        </div>
      ) : null;
      return scopedResponses.length === 0 ? (
        <>
          {scopeBanner}
          <EmptyBlock
            title="No responses yet"
            body="When another agency sends a player for one of your requests, it shows up here."
          />
        </>
      ) : (
        <>
          {scopeBanner}
          {responseGroups(scopedResponses)}
        </>
      );
    }
    if (activeTab === "shortlist") {
      return shortlisted.length === 0 ? (
        <EmptyBlock
          title="Your shortlist is empty"
          body="Shortlist the responses you want to revisit and they'll gather here."
        />
      ) : (
        responseFlatList(shortlisted)
      );
    }
    if (activeTab === "declined") {
      return declined.length === 0 ? (
        <EmptyBlock
          title="No declined responses"
          body="Responses you decline are kept here for your records."
        />
      ) : (
        responseGroups(declined)
      );
    }
    return null;
  };

  return (
    <div className={embedded ? "" : "min-h-full bg-gray-50"}>
      {/* Standalone shell mirrors the AgentTenderFeed container (W1-04):
          no fixed max-width — the page scales to 2560+ with the platform's
          progressive padding ladder, same as the embedded "Mine" tab. */}
      <div
        className={
          embedded
            ? ""
            : "mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6"
        }
      >
        {!embedded && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold">My Requests</h1>
              <p className="text-sm text-gray-500 mt-1">
                Requests posted by you and your agency, and the responses they receive.
              </p>
            </div>
            <button
              onClick={() => navigate("/agent-tenders/create")}
              className="px-4 py-2 bg-[#2283FF] text-white rounded-lg text-sm hover:bg-blue-600"
            >
              Post Request
            </button>
          </div>
        )}

        {/* Tabs */}
        {/* Horizontal tab strip. `overflow-x-auto` alone makes the browser
            coerce overflow-y to auto too, adding a phantom vertical scrollbar;
            pin overflow-y-hidden since only horizontal scroll is intended (A1). */}
        <div className="flex items-center gap-4 md:gap-[22px] border-b border-[#E2E8F0] mb-6 overflow-x-auto overflow-y-hidden">
          {MY_REQUESTS_TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`pb-[9px] text-[13.5px] whitespace-nowrap border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                activeTab === key
                  ? "border-[#2283FF] text-[#0F172A] font-bold"
                  : "border-transparent text-[#64748B] font-medium hover:text-gray-800"
              }`}
            >
              {label}
              {/* Count: blue pill on the active tab, quiet gray otherwise
                  (design doc 03, screen 7b tab strip). */}
              <span
                className={
                  activeTab === key
                    ? "text-[11px] font-extrabold bg-[#2283FF] text-white rounded-full px-[7px] py-px"
                    : "text-[#94A3B8]"
                }
              >
                {tabCount(key)}
              </span>
            </button>
          ))}
        </div>

        {renderTab()}
      </div>

      <AgentTenderPreviewModal
        isOpen={!!previewFor}
        onClose={() => setPreviewFor(null)}
        tender={previewFor}
        variant="mine"
      />

      <VerifyNowModal
        isOpen={!!verifyFor}
        onClose={() => setVerifyFor(null)}
        tender={verifyFor}
        defaultEmail={verifyFor?.club?.contact_person_email || ""}
        // Refresh the rows once armed so the journey flips to "awaiting".
        onArmed={() => fetchMine(pagination?.currentPage || 1)}
      />

      <CloseConfirm
        tender={closeFor}
        busy={closingId === closeFor?.id}
        onCancel={() => setCloseFor(null)}
        onConfirm={async () => {
          const ok = await closeTender(closeFor.id);
          if (ok) setCloseFor(null);
        }}
      />
    </div>
  );
};

export default MyAgentTenders;
